import React, { useCallback, useState } from 'react'
import { Box, Typography, Paper, Popper, CircularProgress, TextField, InputAdornment, IconButton } from '@mui/material'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import SearchIcon from '@mui/icons-material/Search'
import ClearIcon from '@mui/icons-material/Clear'
import { greenTheme } from '../../helpers/style'

const { REACT_APP_SERVER } = process.env
const color = REACT_APP_SERVER === 'ATS' ? '#0b73b9' : greenTheme.primary
const hoverColor = REACT_APP_SERVER === 'ATS' ? '#095a8f' : greenTheme.hover

import {
  DndContext,
  DragOverlay,
  useDraggable,
  useDroppable,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'

export const ZONE_NOT_SELECTED = 'zone-not-selected'
export const ZONE_SELECTED = 'zone-selected'

function SipCard({ sipItem, getItemId }) {
  const id = getItemId(sipItem)
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id,
    data: { sipItem },
  })
  return (
    <Box
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      sx={{
        display: 'flex',
        alignItems: 'center',
        py: 0.75,
        px: 1,
        borderBottom: '1px solid',
        borderColor: 'divider',
        cursor: isDragging ? 'grabbing' : 'grab',
        bgcolor: isDragging ? 'action.hover' : 'transparent',
        opacity: isDragging ? 0.35 : 1,
        transition: 'opacity 0.2s',
        '&:last-of-type': { borderBottom: 'none' },
      }}
    >
      <DragIndicatorIcon sx={{ color: 'text.secondary', mr: 0.5, fontSize: 18 }} />
      <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
        <Typography variant="body2" fontWeight={600} noWrap>
          {sipItem.buddy || sipItem.id} — {sipItem.notes || sipItem.email || ''}
        </Typography>
        {sipItem.provider ? (
          <Typography variant="caption" color="text.secondary">
            {sipItem.provider}
          </Typography>
        ) : null}
      </Box>
    </Box>
  )
}

function matchSipItem(item, q) {
  if (!q || !String(q).trim()) return true
  const s = String(q).toLowerCase().trim()
  const fields = [
    item.buddy,
    item.id,
    item.notes,
    item.email,
    item.provider,
  ].filter(Boolean).map((x) => String(x).toLowerCase())
  return fields.some((f) => f.includes(s))
}

function DropBlock({ id, title, count, children, isEmpty, searchValue, onSearchChange, searchPlaceholder, primaryColor }) {
  const { setNodeRef, isOver } = useDroppable({ id })
  const hasSearch = onSearchChange != null
  const value = searchValue ?? ''
  const c = primaryColor ?? color
  return (
    <Box
      ref={setNodeRef}
      sx={{
        flex: 1,
        minWidth: 0,
        border: '2px solid',
        borderColor: isOver ? c : 'divider',
        borderRadius: 2,
        bgcolor: isOver ? `${c}10` : 'grey.50',
        transition: 'border-color 0.2s, background-color 0.2s',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          py: 1,
          px: 1.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Typography variant="subtitle2" fontWeight={700} color="text.primary">
          {title} ({count})
        </Typography>
        {hasSearch && (
          <TextField
            size="small"
            placeholder={searchPlaceholder}
            value={value}
            onChange={(e) => onSearchChange(e.target.value)}
            sx={{
              mt: 1,
              '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: c },
                '&:hover fieldset': { borderColor: c },
                '&.Mui-focused fieldset': { borderColor: c },
              },
              '& .MuiInputLabel-root.Mui-focused': { color: c },
            }}
            fullWidth
            variant="outlined"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: c }} />
                </InputAdornment>
              ),
              endAdornment: value ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => onSearchChange('')} edge="end" aria-label="clear">
                    <ClearIcon fontSize="small" sx={{ color: c }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
        )}
      </Box>
      <Box
        sx={{
          flex: 1,
          minHeight: 120,
          maxHeight: '40vh',
          overflowY: 'auto',
          p: 0.5,
        }}
      >
        {isEmpty ? (
          <Typography variant="body2" color="text.secondary" textAlign="center" py={3}>
            —
          </Typography>
        ) : (
          children
        )}
      </Box>
    </Box>
  )
}

const helpAnimationSx = {
  display: 'flex',
  gap: 1,
  alignItems: 'stretch',
  minHeight: 72,
  position: 'relative',
  '& .help-drag-zone': {
    flex: 1,
    borderRadius: 1,
    border: '2px dashed',
    borderColor: 'divider',
    bgcolor: 'grey.100',
  },
  '& .help-drag-card': {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    display: 'flex',
    alignItems: 'center',
    gap: 0.5,
    px: 1,
    py: 0.5,
    borderRadius: 1,
    bgcolor: 'background.paper',
    boxShadow: 2,
    border: '1px solid',
    borderColor: color,
    animation: 'helpDragDemo 3.2s ease-in-out infinite',
    whiteSpace: 'nowrap',
  },
  '@keyframes helpDragDemo': {
    '0%, 100%': { left: '12px', transform: 'translateY(-50%)', boxShadow: 2 },
    '12%': { left: '12px', transform: 'translateY(-50%) scale(0.96)', boxShadow: 3 },
    '18%': { left: '12px', transform: 'translate(-50%, -54%) scale(1)', boxShadow: 5 },
    '48%': { left: '50%', transform: 'translate(-50%, -54%)', boxShadow: 5 },
    '56%': { left: 'calc(100% - 12px)', transform: 'translate(-100%, -54%)', boxShadow: 5 },
    '62%': { left: 'calc(100% - 12px)', transform: 'translate(-100%, -46%)', boxShadow: 2 },
    '70%': { left: 'calc(100% - 12px)', transform: 'translate(-100%, -50%)', boxShadow: 2 },
    '82%': { left: 'calc(100% - 12px)', transform: 'translate(-100%, -50%)', boxShadow: 2 },
  },
}

export default function SipDragDropZones({
  items = [],
  selectedIds = [],
  onSelectionChange,
  zoneNotSelectedTitle = 'Not selected',
  zoneSelectedTitle = 'Selected',
  zoneNotSelectedId = ZONE_NOT_SELECTED,
  zoneSelectedId = ZONE_SELECTED,
  getItemId = (item) => item.buddy || String(item.id),
  loading = false,
  emptyMessage = 'No data',
  helpAnchorEl = null,
  onHelpPopperEnter,
  onHelpPopperLeave,
  helpTitle = 'How to use',
  helpDescription = 'Drag items from one column to the other to change selection.',
  helpDragMeText = 'Drag me',
  searchPlaceholder,
}) {
  const [activeId, setActiveId] = useState(null)
  const [searchNotSelected, setSearchNotSelected] = useState('')
  const [searchSelected, setSearchSelected] = useState('')
  const activeItem = activeId && items.find((item) => getItemId(item) === activeId)

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 8 } })
  )

  const handleDragStart = useCallback((event) => {
    setActiveId(event.active.id)
  }, [])

  const handleDragEnd = useCallback(
    (event) => {
      const { active, over } = event
      setActiveId(null)
      if (!over || !onSelectionChange) return
      const id = active.id
      if (over.id === zoneSelectedId) {
        onSelectionChange((prev) => (prev.includes(id) ? prev : [...prev, id]))
      } else if (over.id === zoneNotSelectedId) {
        onSelectionChange((prev) => prev.filter((b) => b !== id))
      }
    },
    [onSelectionChange, zoneNotSelectedId, zoneSelectedId]
  )

  const notSelectedItems = items.filter((item) => !selectedIds.includes(getItemId(item)))
  const selectedItems = items.filter((item) => selectedIds.includes(getItemId(item)))
  const filteredNotSelected = notSelectedItems.filter((item) => matchSipItem(item, searchNotSelected))
  const filteredSelected = selectedItems.filter((item) => matchSipItem(item, searchSelected))

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={32} sx={{ color }} />
      </Box>
    )
  }

  if (!items.length) {
    return (
      <Typography variant="body2" color="text.secondary" textAlign="center" py={3}>
        {emptyMessage}
      </Typography>
    )
  }

  return (
    <>
      {helpAnchorEl != null && (
        <Popper
          open={Boolean(helpAnchorEl)}
          anchorEl={helpAnchorEl}
          placement="bottom-start"
          modifiers={[{ name: 'offset', options: { offset: [0, 8] } }]}
        >
          <Paper
            elevation={8}
            onMouseEnter={onHelpPopperEnter}
            onMouseLeave={onHelpPopperLeave}
            sx={{ mt: 0, overflow: 'visible', width: 400 }}
          >
            <Box sx={{ p: 2 }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
                {helpTitle}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {helpDescription}
              </Typography>
              <Box sx={helpAnimationSx}>
                <Box className="help-drag-zone" />
                <Box className="help-drag-zone" />
                <Box className="help-drag-card">
                  <DragIndicatorIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                  <Typography variant="caption" fontWeight={600}>
                    {helpDragMeText}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Paper>
        </Popper>
      )}

      <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <DropBlock
            id={zoneNotSelectedId}
            primaryColor={color}
            title={zoneNotSelectedTitle}
            count={notSelectedItems.length}
            isEmpty={!filteredNotSelected.length}
            searchValue={searchPlaceholder != null ? searchNotSelected : undefined}
            onSearchChange={searchPlaceholder != null ? setSearchNotSelected : undefined}
            searchPlaceholder={searchPlaceholder}
          >
            {(searchPlaceholder != null ? filteredNotSelected : notSelectedItems).map((sipItem) => (
              <SipCard key={getItemId(sipItem)} sipItem={sipItem} getItemId={getItemId} />
            ))}
          </DropBlock>
          <DropBlock
            id={zoneSelectedId}
            primaryColor={color}
            title={zoneSelectedTitle}
            count={selectedItems.length}
            isEmpty={!filteredSelected.length}
            searchValue={searchPlaceholder != null ? searchSelected : undefined}
            onSearchChange={searchPlaceholder != null ? setSearchSelected : undefined}
            searchPlaceholder={searchPlaceholder}
          >
            {(searchPlaceholder != null ? filteredSelected : selectedItems).map((sipItem) => (
              <SipCard key={getItemId(sipItem)} sipItem={sipItem} getItemId={getItemId} />
            ))}
          </DropBlock>
        </Box>

        <DragOverlay
          dropAnimation={{
            duration: 200,
            easing: 'cubic-bezier(0.18, 0.67, 0.6, 1.22)',
          }}
        >
          {activeItem ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                py: 0.75,
                px: 1.5,
                borderRadius: 1,
                boxShadow: 3,
                bgcolor: 'background.paper',
                border: '2px solid',
                borderColor: color,
                cursor: 'grabbing',
                minWidth: 200,
              }}
            >
              <DragIndicatorIcon sx={{ color: 'text.secondary', mr: 0.5, fontSize: 18 }} />
              <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
                <Typography variant="body2" fontWeight={600} noWrap>
                  {activeItem.buddy || activeItem.id} — {activeItem.notes || activeItem.email || ''}
                </Typography>
                {activeItem.provider ? (
                  <Typography variant="caption" color="text.secondary">
                    {activeItem.provider}
                  </Typography>
                ) : null}
              </Box>
            </Box>
          ) : null}
        </DragOverlay>
      </DndContext>
    </>
  )
}
